import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getBalance, getAdminBalanceForSubadmin, getSystemConfig, getMidasStatus, getAltStatus, getServicesStatus, getLogos } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Wallet, 
  Smartphone, 
  Wifi, 
  Zap, 
  FileText, 
  Shield, 
  CreditCard, 
  History, 
  Settings,
  ArrowUpRight,
  Copy,
  MessageCircle,
  Info,
  DollarSign,
  RefreshCw,
  Globe,
  Send,
  Tv,
  Gift,
  Gamepad2
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import PromotionalBalance from '@/components/PromotionalBalance';

const WHATSAPP_SUPPORT = process.env.REACT_APP_WHATSAPP_SUPPORT || '+18297091155';

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminBalanceInfo, setAdminBalanceInfo] = useState({ balance: null, can_view: false });
  const [subadminCanRequestBalance, setSubadminCanRequestBalance] = useState(false);
  const [midasStatus, setMidasStatus] = useState(null); // null = loading
  const [refreshingMidas, setRefreshingMidas] = useState(false);
  const [altStatus, setAltStatus] = useState(null);
  const [refreshingAlt, setRefreshingAlt] = useState(false);
  const [servicesStatus, setServicesStatus] = useState({});
  const [dashboardLogo, setDashboardLogo] = useState('/recargameya-logo.jpg');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const isSubAdmin = user?.role === 'sub_admin';
  const isAdmin = user?.role === 'admin';
  const isApiClient = user?.role === 'api_client';

  useEffect(() => {
    loadBalance();
    loadServicesStatus();
    loadDashboardLogo();
  }, [user?.role]);

  // Load dashboard logo
  const loadDashboardLogo = async () => {
    try {
      const logos = await getLogos();
      if (logos.dashboard_logo) {
        setDashboardLogo(`${BACKEND_URL}/api/uploads/logos/${logos.dashboard_logo}`);
      }
    } catch (error) {
      console.error('Error loading dashboard logo:', error);
    }
  };

  // Load services status
  const loadServicesStatus = async () => {
    try {
      const data = await getServicesStatus();
      setServicesStatus(data);
    } catch (error) {
      console.error('Error loading services status:', error);
    }
  };

  // Polling automático del balance cada 15 segundos
  useEffect(() => {
    const pollBalance = async () => {
      try {
        const data = await getBalance();
        setBalance(data);
        await refreshUser();
      } catch (error) {
        console.error('Error polling balance:', error);
      }
    };

    if (user) {
      const intervalId = setInterval(pollBalance, 15000);
      return () => clearInterval(intervalId);
    }
  }, [user]);

  const loadBalance = async () => {
    try {
      const data = await getBalance();
      setBalance(data);
      await refreshUser();
      
      // If admin, load Midas status
      if (user?.role === 'admin') {
        try {
          const midasData = await getMidasStatus();
          setMidasStatus(midasData);
        } catch (e) {
          setMidasStatus(null);
        }
        try {
          const altData = await getAltStatus();
          setAltStatus(altData);
        } catch (e) {
          setAltStatus(null);
        }
      }
      
      // If sub-admin, also load admin balance and config
      if (user?.role === 'sub_admin') {
        try {
          const [adminBalance, config] = await Promise.all([
            getAdminBalanceForSubadmin(),
            getSystemConfig()
          ]);
          setAdminBalanceInfo(adminBalance);
          setSubadminCanRequestBalance(config.subadmin_can_request_balance || false);
        } catch (e) {
          setAdminBalanceInfo({ balance: null, can_view: false });
          setSubadminCanRequestBalance(false);
        }
      }
    } catch (error) {
      console.error('Error loading balance:', error);
      toast.error('Error al cargar el balance');
    } finally {
      setLoading(false);
    }
  };

  const refreshMidasBalance = async () => {
    setRefreshingMidas(true);
    try {
      const midasData = await getMidasStatus();
      setMidasStatus(midasData);
      toast.success('Balance de Midas actualizado');
    } catch (e) {
      toast.error('Error al actualizar balance de Midas');
    } finally {
      setRefreshingMidas(false);
    }
  };

  const refreshAltBalance = async () => {
    setRefreshingAlt(true);
    try {
      const altData = await getAltStatus();
      setAltStatus(altData);
      toast.success('Balance de UTP actualizado');
    } catch (e) {
      toast.error('Error al actualizar balance de UTP');
    } finally {
      setRefreshingAlt(false);
    }
  };

  const copyId = () => {
    if (user?.id) {
      navigator.clipboard.writeText(user.id);
      toast.success('ID copiado al portapapeles');
    }
  };

  const services = [
    {
      title: 'Recargas',
      description: 'Móvil, apuestas y más',
      icon: Smartphone,
      color: 'from-blue-500 to-cyan-500',
      type: 'recarga_movil'
    },
    {
      title: 'Paquetes Internet',
      description: 'Datos móviles',
      icon: Wifi,
      color: 'from-purple-500 to-pink-500',
      type: 'paquete_internet'
    },
    {
      title: 'Luz Prepago',
      description: 'Edenorte, Edesur',
      icon: Zap,
      color: 'from-yellow-500 to-orange-500',
      type: 'luz_prepago'
    },
    {
      title: 'Remesas Haiti',
      description: 'MonCash, NatCash',
      icon: Send,
      color: 'from-violet-500 to-purple-500',
      type: 'remesas'
    },
    {
      title: 'Juegos/Otros',
      description: 'Free Fire, Roblox, giftcards y más',
      icon: Gamepad2,
      color: 'from-orange-500 to-amber-500',
      type: 'juegos'
    },
    {
      title: 'Pago de Facturas',
      description: 'Luz, agua, teléfono, cable',
      icon: FileText,
      color: 'from-teal-500 to-emerald-500',
      type: 'factura'
    },
    {
      title: 'Seguro Vehículo',
      description: 'Seguros vehiculares',
      icon: Shield,
      color: 'from-red-500 to-rose-500',
      type: 'seguro_midas'
    },
    {
      title: 'IPTV',
      description: 'Televisión por internet',
      icon: Tv,
      color: 'from-teal-500 to-cyan-500',
      type: 'iptv'
    },
    {
      title: 'Gift Cards',
      description: 'Netflix, Amazon y más',
      icon: Gift,
      color: 'from-pink-500 to-rose-500',
      type: 'gift_card'
    },
    {
      title: 'Recargas Int.',
      description: 'Envía recargas al exterior',
      icon: Globe,
      color: 'from-indigo-500 to-blue-600',
      type: 'recarga_internacional'
    },
    {
      title: 'Activación SIM',
      description: 'Claro, Viva, Altice',
      icon: CreditCard,
      color: 'from-slate-500 to-slate-600',
      type: 'sim_activation'
    }
  ];

  // Filter services based on enabled status (and hide all services for sub-admins
  // — they can only do administrative tasks, never run real-money operations)
  // Also hide categories that admin has individually blocked for this user.
  const userBlockedServices = Array.isArray(user?.blocked_services) ? user.blocked_services : [];
  const availableServices = ((user?.role === 'sub_admin' || user?.role === 'api_client') ? [] : services).filter(service => {
    const status = servicesStatus[service.type];
    if (status && status.enabled === false) {
      return false;
    }
    if (userBlockedServices.includes(service.type)) {
      return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen pb-4 sm:pb-8">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 mt-4 sm:mt-8">
        {/* Welcome Section - Compacto en móvil */}
        <div className="mb-4 sm:mb-8 animate-fadeIn">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-3xl font-bold text-slate-900 truncate" data-testid="dashboard-welcome">
                ¡Hola, {user?.business_name}! 👋
              </h1>
              <p className="text-xs sm:text-base text-slate-600">Bienvenido a tu panel de control</p>
              
              {/* ID compacto */}
              <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                <div className="bg-slate-100 px-2 py-1 rounded flex items-center gap-1">
                  <span className="text-slate-500">Su ID es:</span>
                  <code className="font-mono font-bold text-slate-800" data-testid="dashboard-user-id">
                    {user?.id}
                  </code>
                  <button 
                    onClick={copyId}
                    className="text-slate-400 hover:text-slate-600 transition-colors ml-1"
                    data-testid="dashboard-copy-id-button"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Logo on Desktop - right side */}
            <div className="hidden sm:flex items-center justify-center">
              <img 
                src={dashboardLogo} 
                alt="RecargameYa" 
                className="h-24 w-auto rounded-lg shadow-md"
                onError={(e) => { e.target.src = '/recargameya-logo.jpg'; }}
              />
            </div>
          </div>
        </div>

        {/* Balance Card - Compacto en móvil */}
        <div className="mb-4 sm:mb-8 animate-fadeIn">
          <Card className="glass balance-glow border-2 border-blue-200 overflow-hidden" data-testid="dashboard-balance-card">
            <div className={`p-3 sm:p-6 text-white ${isApiClient ? 'bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600' : (isSubAdmin && adminBalanceInfo.can_view ? 'bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-600' : 'bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600')}`}>
              {isApiClient ? (
                <div>
                  <div className="flex items-center gap-2 sm:gap-3 mb-3">
                    <div className="bg-white/20 backdrop-blur-sm p-2 sm:p-3 rounded-xl">
                      <Wallet className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-emerald-100">Cuenta API</p>
                      <h2 className="text-base sm:text-2xl font-bold" data-testid="api-client-business-name">
                        {user?.business_name || 'Cliente API'}
                      </h2>
                    </div>
                    <Badge className="ml-auto bg-white/20 text-white text-xs">API Client</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4" data-testid="api-client-balance-sandbox-card">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-emerald-100">Sandbox</span>
                        <Badge className="bg-amber-400 text-amber-900 text-[10px] px-1.5 py-0">TEST</Badge>
                      </div>
                      <p className="text-lg sm:text-2xl font-bold" data-testid="api-client-balance-sandbox">
                        RD$ {(user?.balance_sandbox || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-[10px] sm:text-xs text-emerald-100/80 mt-1">No consume dinero real</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4" data-testid="api-client-balance-live-card">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-emerald-100">Producción</span>
                        <Badge className="bg-emerald-400 text-emerald-900 text-[10px] px-1.5 py-0">LIVE</Badge>
                      </div>
                      <p className="text-lg sm:text-2xl font-bold" data-testid="api-client-balance-live">
                        RD$ {(user?.balance || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-[10px] sm:text-xs text-emerald-100/80 mt-1">Prepago real</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 bg-white/10 backdrop-blur-sm rounded-lg p-3 mt-3">
                    <Info className="w-4 h-4 text-emerald-200 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-emerald-100 leading-relaxed">
                      Las operaciones se ejecutan únicamente por la API pública con firma HMAC. Para recargar balance de producción, contacta al equipo de RecargameYa.
                    </p>
                  </div>
                </div>
              ) : (
              <>
              <div className="flex items-center justify-between gap-2 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 backdrop-blur-sm p-2 sm:p-3 rounded-xl">
                    <Wallet className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    {isSubAdmin && adminBalanceInfo.can_view ? (
                      <>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-xs sm:text-sm text-blue-100">Balance del Admin</p>
                          <Badge className="bg-white/20 text-white text-xs hidden sm:inline-flex">Admin</Badge>
                        </div>
                        <h2 className="text-xl sm:text-4xl font-bold" data-testid="dashboard-balance-amount">
                          {loading ? '...' : `RD$ ${(adminBalanceInfo.balance || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        </h2>
                      </>
                    ) : isSubAdmin && !adminBalanceInfo.can_view ? (
                      <>
                        <p className="text-xs sm:text-sm text-blue-100">Balance</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Info className="w-4 h-4 sm:w-5 sm:h-5 text-blue-200" />
                          <p className="text-base sm:text-lg text-blue-100">No disponible</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-xs sm:text-sm text-blue-100">Balance Disponible</p>
                        {user?.balance_visible === false ? (
                          <div className="flex items-center gap-2 mt-1" data-testid="dashboard-balance-hidden">
                            <Wallet className="w-6 h-6 sm:w-9 sm:h-9 text-white/90" strokeWidth={2} />
                          </div>
                        ) : (
                          <h2 className="text-xl sm:text-4xl font-bold" data-testid="dashboard-balance-amount">
                            {loading ? '...' : `RD$ ${balance?.balance?.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`}
                          </h2>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {(!isSubAdmin || (isSubAdmin && subadminCanRequestBalance)) && !isApiClient && (
                  <Button 
                    onClick={() => navigate('/recharge-balance')} 
                    className="bg-white text-blue-600 hover:bg-blue-50 text-xs sm:text-sm px-3 sm:px-4"
                    data-testid="dashboard-recharge-button"
                  >
                    <ArrowUpRight className="w-4 h-4 mr-1 sm:mr-2" />
                    Recargar
                  </Button>
                )}
              </div>
              
              {/* Balance History Button */}
              {(!isSubAdmin || (isSubAdmin && subadminCanRequestBalance)) && !isApiClient && (
                <div className="flex justify-end mt-2">
                  <Button 
                    onClick={() => navigate('/balance-history')} 
                    variant="ghost"
                    className="text-white/80 hover:text-white hover:bg-white/10 text-xs sm:text-sm h-8 px-2 sm:px-4"
                    data-testid="dashboard-balance-history-button"
                  >
                    <History className="w-4 h-4 mr-1 sm:mr-2" />
                    Ver Movimientos
                  </Button>
                </div>
              )}
              
              {/* Sub-admin notice - Solo desktop */}
              {isSubAdmin && adminBalanceInfo.can_view && (
                <div className="hidden sm:flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg p-3 mt-4">
                  <Info className="w-5 h-5 text-blue-200" />
                  <p className="text-sm text-blue-100">
                    Las aprobaciones y transferencias de balance se descuentan de este monto
                  </p>
                </div>
              )}
              </>
              )}
            </div>
          </Card>
        </div>

        {/* Promotional Balance Card - No aplica para api_client */}
        {!isApiClient && (
        <div className="mb-4 sm:mb-8 animate-fadeIn">
          <PromotionalBalance />
        </div>
        )}

        {/* Midas API Balance - Only for Admin */}
        {isAdmin && midasStatus && (
          <div className="mb-3 animate-fadeIn">
            <Card className={`glass border-2 overflow-hidden ${midasStatus.connected ? 'border-green-200' : 'border-yellow-200'}`} data-testid="dashboard-midas-balance-card">
              <div className={`p-3 sm:p-4 text-white ${midasStatus.connected ? 'bg-gradient-to-br from-green-600 via-emerald-600 to-teal-600' : 'bg-gradient-to-br from-yellow-600 via-amber-600 to-orange-600'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 backdrop-blur-sm p-2.5 rounded-xl">
                      <DollarSign className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm text-white/80">Balance API Midas</p>
                        <Badge className={`text-xs ${midasStatus.simulation_mode ? 'bg-yellow-400 text-yellow-900' : midasStatus.connected ? 'bg-white/20 text-white' : 'bg-red-400 text-red-900'}`}>
                          {midasStatus.simulation_mode ? '🔄 Simulación' : midasStatus.connected ? '✓ Conectado' : '✗ Desconectado'}
                        </Badge>
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold mt-0.5" data-testid="dashboard-midas-balance">
                        {midasStatus.connected 
                          ? `${midasStatus.currency || 'RD$'} ${(midasStatus.balance || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : midasStatus.simulation_mode ? 'Modo Simulación' : 'No disponible'
                        }
                      </h2>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={refreshMidasBalance}
                      disabled={refreshingMidas}
                      className="bg-white/20 hover:bg-white/30 text-white"
                      size="icon"
                      data-testid="refresh-midas-balance-btn"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshingMidas ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
                {midasStatus.simulation_mode && (
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg p-2 mt-3">
                    <Info className="w-4 h-4 text-white/80" />
                    <p className="text-xs text-white/80">
                      Las transacciones no son reales en modo simulación
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Alt Provider (UTP) Balance - Only for Admin */}
        {isAdmin && altStatus && (
          <div className="mb-8 animate-fadeIn">
            <Card className={`glass border-2 overflow-hidden ${altStatus.connected ? 'border-blue-200' : 'border-yellow-200'}`} data-testid="dashboard-alt-balance-card">
              <div className={`p-3 sm:p-4 text-white ${altStatus.connected ? 'bg-gradient-to-br from-blue-600 via-cyan-600 to-sky-600' : 'bg-gradient-to-br from-yellow-600 via-amber-600 to-orange-600'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 backdrop-blur-sm p-2.5 rounded-xl">
                      <Wallet className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm text-white/80">Balance API UTP</p>
                        <Badge className={`text-xs ${altStatus.connected ? 'bg-white/20 text-white' : 'bg-red-400 text-red-900'}`}>
                          {altStatus.connected ? '✓ Conectado' : '✗ Desconectado'}
                        </Badge>
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold mt-0.5" data-testid="dashboard-alt-balance">
                        {altStatus.connected
                          ? `${altStatus.currency || 'RD$'} ${(altStatus.balance || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : 'No disponible'
                        }
                      </h2>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={refreshAltBalance}
                      disabled={refreshingAlt}
                      className="bg-white/20 hover:bg-white/30 text-white"
                      size="icon"
                      data-testid="refresh-alt-balance-btn"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshingAlt ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* API Client Quick Actions — vista read-only */}
        {isApiClient && (
          <div className="mb-4 sm:mb-8 animate-fadeIn">
            <h2 className="text-lg sm:text-2xl font-bold text-slate-900 mb-3 sm:mb-4" data-testid="api-client-actions-title">Mi cuenta</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <Card
                className="glass cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                onClick={() => navigate('/history')}
                data-testid="api-client-quick-history"
              >
                <CardContent className="p-4 sm:p-5">
                  <div className="inline-flex p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 mb-3">
                    <History className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-sm sm:text-base text-slate-900 mb-1">Historial de transacciones</h3>
                  <p className="text-xs text-slate-500">Todas las TX ejecutadas por tu API. Filtros y detalle.</p>
                </CardContent>
              </Card>
              <Card
                className="glass"
                data-testid="api-client-quick-docs"
              >
                <CardContent className="p-4 sm:p-5">
                  <div className="inline-flex p-2.5 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 mb-3">
                    <Info className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-sm sm:text-base text-slate-900 mb-1">Documentación API</h3>
                  <p className="text-xs text-slate-500">La API se consume solo con firma HMAC. Consulta la guía técnica que te compartimos por correo.</p>
                </CardContent>
              </Card>
              <Card
                className="glass"
                data-testid="api-client-quick-support"
              >
                <CardContent className="p-4 sm:p-5">
                  <div className="inline-flex p-2.5 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 mb-3">
                    <Wallet className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-sm sm:text-base text-slate-900 mb-1">Soporte técnico</h3>
                  <p className="text-xs text-slate-500">soporte@recargameya.com · respuesta &lt;24h laborables</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Services Grid - 2 columnas en móvil, 3 en desktop */}
        {availableServices.length > 0 && (
        <div className="mb-4 sm:mb-8">
          <h2 className="text-lg sm:text-2xl font-bold text-slate-900 mb-3 sm:mb-4" data-testid="dashboard-services-title">Servicios</h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {availableServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index}
                  className="glass cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden group relative"
                  onClick={() => navigate('/services', { state: { serviceType: service.type } })}
                  data-testid={`dashboard-service-${service.type}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex-1">
                        <div className={`inline-flex p-2.5 sm:p-3 rounded-xl bg-gradient-to-br ${service.color} mb-2.5 sm:mb-3 group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className="w-6 h-6 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <h3 className="font-semibold text-sm sm:text-lg text-slate-900 mb-1 leading-tight">{service.title}</h3>
                        <p className="text-xs text-slate-500 sm:text-sm sm:text-slate-600">{service.description}</p>
                      </div>
                      <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 group-hover:text-blue-600 transition-colors absolute top-3 right-3 sm:static" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
